// ============================================================
// MAP READING - what the tactical map says about one combatant, for
// one attacker: the cover it sees, what the ground adds and lends, the
// facing a vehicle presents. `projectDefenderFrom` reads it and lays it
// over the base projection; nothing here is ever stored.
//
// Split out of projection.ts, which keeps the caches. Pure. Zero React
// dependency.
// ============================================================
import type { Cover, Target } from '../../types';
import { coverLevelsDenied, stepCoverDown } from '../exposures';
import type { Combatant } from './types';
import { isImmuneTo } from '../conditionEligibility';
import { resolveCoverConflict } from '../coverConflict';
import type { CoverConflictMode } from '../coverConflict';
import { condType, condDuration } from '../conditions';
import type { VehicleFacing } from '../vehicleFacing';

/** A cover as a level and the AP that came with it. */
type CoverPair = { level: Cover['level']; ap: number };

/** What the map says about one combatant, for one attacker. */
export interface MapReading {
  manual: Cover | undefined;
  resolved: ReturnType<typeof resolveCoverConflict> | null;
  /** The map's cover beat the hand-set one (or there was none). */
  coverWins: boolean;
  /** Conditions the ground adds. */
  added: string[];
  /** Traits the ground lends. */
  lent: Record<string, boolean | number>;
  /** Every trait the ground carries, lent or not - part of the cache key. */
  traits: Record<string, boolean | number>;
  surfaceCover: CoverPair | null;
  mapFacing: VehicleFacing | null | undefined;
}

export interface MapInputs {
  derivedCover?: Cover | null;
  surface?: SurfaceLike | null;
  coverMode: CoverConflictMode;
  mapFacing?: VehicleFacing | null;
}

/**
 * TWO THINGS CAN CLAIM TO GIVE COVER, and the table decides how they
 * meet. This used to be `derived wins only if nothing is pinned`, which
 * meant a value set by hand DELETED the map's - "5 cover to the legs"
 * against a wall left the legs covered and the wall discarded (v8.13.0).
 * See `engine/coverConflict.ts`.
 */
export function readMap(
  c: Combatant,
  base: Target,
  { derivedCover, surface, coverMode, mapFacing }: MapInputs,
): MapReading {
  const manual = c.state?.cover;
  const resolved = derivedCover ? resolveCoverConflict(manual, derivedCover, coverMode) : null;
  const traits = surface?.traits ?? {};
  return {
    manual,
    resolved,
    coverWins: !!resolved && resolved.cover !== manual,
    added: groundConditions(base, surface?.grants ?? []),
    lent: lentTraits(base, traits),
    traits,
    surfaceCover: surfaceCoverOf(surface),
    mapFacing,
  };
}

/**
 * A MAP FACING IS A REASON TO PROJECT ALL BY ITSELF. This bail-out once
 * knew only about cover, the ground and traits - so a tank in the open,
 * with nothing else to say about it, returned the bare projection and
 * the arc was silently dropped.
 */
export function changesNothing(r: MapReading): boolean {
  if (r.coverWins || r.surfaceCover || r.mapFacing) return false;
  return r.added.length === 0 && Object.keys(r.lent).length === 0;
}

/**
 * The conditions the ground adds: only codes they do not already carry.
 *
 * A combatant genuinely On Fire who then steps into burning ground must
 * not have their own condition stripped when they step out - so what the
 * ground ADDED is recorded exactly, and nothing else is touched.
 */
function groundConditions(base: Target, grants: string[]): string[] {
  const own = new Set(base.conditions ?? []);
  return grants.filter((code) => groundMayGrant(base, own, code));
}

/**
 * Not what they already carry, and NOT WHAT THEY ARE IMMUNE TO. A Machine
 * standing in burning promethium should not catch fire: `IMMUNITY_MAP`
 * has said so since long before there was a map, and downstream honours
 * it - so the effect was already nil, but the condition appeared on the
 * sheet, which reads as a bug. A condition forced on by hand is still
 * allowed; that is a GM's ruling, and this is the ground's.
 *
 * Haywire fields never stack (v8.199.8, same ruling as mergeCondition): a
 * ground field is IGNORED while the combatant already carries an
 * equal-or-stronger one. A STRONGER ground field is granted alongside the
 * stored one - the stronger chip dominates, and the weapon's own weaker
 * field resumes its decay when they step off the surface.
 */
function groundMayGrant(base: Target, own: Set<string>, code: string): boolean {
  if (own.has(code) || isImmuneTo(base as unknown as Record<string, unknown>, condType(code))) return false;
  if (condType(code) !== 'Hw') return true;
  const groundSev = condDuration(code);
  return ![...own].some((held) => condType(held) === 'Hw' && condDuration(held) >= groundSev);
}

/**
 * What the GROUND is lending, and only that: a trait the combatant
 * already has is theirs, not the mire's, and saying otherwise would put
 * "Undying, from the ground" on a man who has always been Undying.
 */
function lentTraits(base: Target, traits: Record<string, boolean | number>): Record<string, boolean | number> {
  return Object.fromEntries(
    Object.entries(traits).filter(([field, value]) => {
      if (!value) return false;
      const own = (base as unknown as Record<string, unknown>)[field];
      return typeof value === 'number' ? !(typeof own === 'number' && own >= value) : !own;
    }),
  );
}

/**
 * A COVER IS A PAIR. Taking a level from one source and an AP from
 * another produced `{ level: 'none', ap: 4 }` whenever the line of sight
 * was clear - and `isLocCoveredForBody('none', …)` is false, so the AP
 * was never read and a surface's cover did nothing at all (v7.66.0). A
 * surface with no LEVEL offers no cover, whatever AP it carries: absent
 * must mean absent, which is what zones written before this existed rely
 * on.
 */
function surfaceCoverOf(surface: SurfaceLike | null | undefined): CoverPair | null {
  if (!surface?.coverLevel || surface.coverLevel === 'none') return null;
  return { level: surface.coverLevel, ap: surface.coverAp ?? 0 };
}

/** A hand-set cover counts as a pin only when it says something - `none` is no ruling. */
function pinnedCover(manual: Cover | undefined): Cover | null {
  return manual && manual.level !== 'none' ? manual : null;
}

/** `level:ap`, or `-` for no cover - one field of the cache key. */
function pairKey(cover: CoverPair | null | undefined): string {
  return cover ? `${cover.level}:${cover.ap}` : '-';
}

/** Everything that can change the derived Target, as one string. */
export function readingKey(r: MapReading): string {
  return [
    r.coverWins ? pairKey(r.resolved!.cover) : '-',
    pairKey(r.resolved?.alt),
    pairKey(pinnedCover(r.manual)),
    pairKey(r.surfaceCover),
    r.added.join('|'),
    r.mapFacing ?? '-',
    Object.entries(r.traits)
      .map(([k, v]) => `${k}=${v}`)
      .sort()
      .join('|'),
  ].join('#');
}
/**
 * The cover the combatant ends up wearing.
 *
 * The ground's cover competes with the map's ON THE USUAL TERMS: the
 * better of the two, as a pair, because cover is never cumulative and a
 * level only means anything beside the AP it came with.
 *
 * AN EXPOSURE CAN DENY COVER (v8.349.0): Flushed out steps the level the
 * map found down by N, never below none - and none is no cover. Read
 * without the attacker's team: this projection knows the map, not who is
 * looking, so any scope on a cover axis reads as all (`coverLevelsDenied`).
 */
function coverWorn(base: Target, r: MapReading): CoverPair | null {
  const fromMap = r.coverWins ? { level: r.resolved!.cover.level, ap: r.resolved!.cover.ap } : null;
  const picked = pickCover(fromMap, r.surfaceCover);
  const denied = coverLevelsDenied({ conditions: base.conditions, exposures: base.exposures });
  const stepped = picked && denied ? { ...picked, level: stepCoverDown(picked.level, denied) } : picked;
  return stepped && stepped.level !== 'none' ? stepped : null;
}

/**
 * What rides along when the map's cover won: under "the best for each
 * body part" the OTHER cover, so the damage roll can ask each of them
 * about the part that was hit; and the pin the map outranked, so the
 * round trip can put it back rather than dropping it to nothing.
 */
function outrankedCover(r: MapReading) {
  if (!r.coverWins) return {};
  const pin = pinnedCover(r.manual);
  return {
    ...(r.resolved?.alt ? { coverAlt: r.resolved.alt } : {}),
    ...(pin ? { coverPinned: { ...pin } } : {}),
  };
}

/** The base projection wearing what the map says. */
export function wearReading(base: Target, r: MapReading): Target {
  const best = coverWorn(base, r);
  return {
    ...base,
    // What the ground LENDS - never the raw traits. The raw spread put a
    // switched-off trait's `false` over the combatant's own number, and
    // Regeneration 1 ground over a Regeneration 3 combatant lowered them
    // (v8.310.9). Ground can only ever add.
    ...r.lent,
    ...(best ? { cover: { level: best.level, ap: best.ap, source: 'derived' } } : {}),
    ...outrankedCover(r),
    // WHAT THE MAP SAYS, FOR THIS ATTACKER ONLY. It rides here and is
    // never stored - `stateFromTarget` builds `CombatState` field by
    // field and does not know this field's name, so it cannot be
    // persisted by construction (v8.52.0).
    ...(r.mapFacing ? { vehicleFacingFromMap: r.mapFacing } : {}),
    ...(r.added.length ? { conditions: [...(base.conditions ?? []), ...r.added], derivedConditions: r.added } : {}),
    // RECORDED, like `derivedConditions`. Traits reach the engine simply
    // by being spread onto the projection - Regeneration heals, Fear
    // frightens - but nothing on the sheet says WHERE they came from, so
    // ground that lent Undying looked, and reads, like nothing happening
    // at all (v7.77.0).
    ...(Object.keys(r.lent).length ? { derivedTraits: r.lent } : {}),
  } as Target;
}

/**
 * The better of two covers, kept whole.
 *
 * By AP, with the level that came with it. Never by level alone and never
 * field by field - that is the mistake this exists to prevent.
 */
function pickCover(
  a: { level: Cover['level']; ap: number } | null,
  b: { level: Cover['level']; ap: number } | null,
): { level: Cover['level']; ap: number } | null {
  if (!a) return b;
  if (!b) return a;
  return b.ap > a.ap ? b : a;
}

/** What `surfaceEffects.ts` returns, without importing the battlefield. */
export interface SurfaceLike {
  grants?: string[];
  traits?: Record<string, boolean | number>;
  coverAp?: number;
  coverLevel?: Cover['level'];
}
